import React, { useState, useEffect } from "react";
import { Flex, FormDialog, TextField, Typography, Select, MenuItem, Box, Button } from "flux";
import { BaseDialogProps } from "@adora/shared/contexts/dialog-context";
import useMetaCTATypes from "@adora/shared/hooks/use-meta-cta-types";
import useGoogleCTATypes from "@adora/shared/hooks/use-google-cta-types";
import useMSANCTATypes from "@adora/shared/hooks/use-msan-cta-types";
import { AdAssetData } from "../../types/ad-asset";
import {
  PublisherPlatforms,
  EnabledPlatforms,
  FacebookPlacementPositions,
  FacebookPlacementSettings,
  InstagramPlacementPositions,
  MessengerPlacementPositions,
  AudienceNetworkPlacementPositions
} from "@adora/shared/features/ad-publishing/types";
import { useDialog } from "@adora/shared/hooks/use-dialog";
import { PositionsMap } from "@adora/shared/features/ad-publishing/helpers";
import { TRACKING_ID_PLACEHOLDER } from "@adora/shared/utils/constants";
import PlatformCheckboxes from "@adora/shared/components/ad-asset-group-common/platform-checkboxes";
import ConfirmationDialog from "@adora/shared/components/confirm-dialog";
import { bulkUpdateAdAssets } from "../../features/ad-publishing/api";
import useAdAssets from "../../hooks/use-ad-assets";
import uploadFile from "../../utils/upload-ad-asset";
import ErrorDialog from "../error-dialog";
import useAdAssetGroup from "../../hooks/use-ad-asset-group";
import { PlatformEnum } from "@adora/shared/types/ad-platforms";
import { useTrackingIdInclusion } from "@adora/shared";
import DestinationUrlField from "@adora/shared/components/ad-asset-group-common/destination-url-field";

interface BulkUpdateAdAssetDialogProps extends BaseDialogProps {
  brandId: number;
  adAssetGroupId: number;
}

interface AdAssetFormData extends Omit<AdAssetData, "facebook_placement_settings"> {
  facebook_placement_settings: FacebookPlacementSettings;
}

const defaultFormData: AdAssetFormData = {
  google_headlines: Array.from({ length: 5 }, () => ""),
  google_long_headline: "",
  google_descriptions: Array.from({ length: 5 }, () => ""),
  google_call_to_action_type: "",
  google_destination_url: TRACKING_ID_PLACEHOLDER,
  facebook_headlines: Array.from({ length: 5 }, () => ""),
  facebook_bodies: Array.from({ length: 5 }, () => ""),
  facebook_description: "",
  facebook_call_to_action_type: "",
  facebook_destination_url: TRACKING_ID_PLACEHOLDER,
  facebook_placement_settings: {
    platforms: {
      [PublisherPlatforms.FACEBOOK]: true,
      [PublisherPlatforms.INSTAGRAM]: true,
      [PublisherPlatforms.AUDIENCE_NETWORK]: true,
      [PublisherPlatforms.MESSENGER]: true
    },
    facebook_positions: Object.values(FacebookPlacementPositions),
    instagram_positions: Object.values(InstagramPlacementPositions),
    audience_network_positions: Object.values(AudienceNetworkPlacementPositions),
    messenger_positions: Object.values(MessengerPlacementPositions)
  },
  msan_headlines: Array.from({ length: 5 }, () => ""),
  msan_long_headline: "",
  msan_descriptions: Array.from({ length: 5 }, () => ""),
  msan_call_to_action_type: "",
  msan_destination_url: TRACKING_ID_PLACEHOLDER,
  name: TRACKING_ID_PLACEHOLDER
};

const BulkUpdateAdAssetDialog: React.FC<BulkUpdateAdAssetDialogProps> = ({
  open,
  onClose,
  brandId,
  adAssetGroupId
}) => {
  const { adAssets, isLoading: isLoadingAdAsset, error: adAssetError } = useAdAssets(adAssetGroupId, Number(brandId));
  const [formData, setFormData] = useState<AdAssetFormData>(defaultFormData);
  const [isPlacementExpanded, setIsPlacementExpanded] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [updating, setUpdating] = useState(false);
  const { adAssetGroup } = useAdAssetGroup(adAssetGroupId, Number(brandId));

  // Use the shared tracking ID inclusion hook
  const { trackingIdInclusion, handleDestinationUrlChange, handleTrackingIdInclusionChange } = useTrackingIdInclusion({
    onFieldChange: (field: string, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  });

  const { ctaTypes: metaCtaTypes, isLoading: isLoadingMetaCTATypes, error: metaCtaTypesError } = useMetaCTATypes();
  const {
    ctaTypes: googleCtaTypes,
    isLoading: isLoadingGoogleCTATypes,
    error: googleCtaTypesError
  } = useGoogleCTATypes();
  const { ctaTypes: msanCtaTypes, isLoading: isLoadingMSANCTATypes, error: msanCtaTypesError } = useMSANCTATypes();

  const { openDialog } = useDialog();

  useEffect(() => {
    if (adAssets && adAssets.length > 0) {
      const adAsset = adAssets[0];
      setFormData((prev) => ({
        ...prev,
        google_headlines: mergeArrays(prev.google_headlines, adAsset.google_headlines),
        google_long_headline: adAsset.google_long_headline ?? prev.google_long_headline,
        google_descriptions: mergeArrays(prev.google_descriptions, adAsset.google_descriptions),
        google_destination_url: adAsset.google_destination_url ?? prev.google_destination_url,
        facebook_headlines: mergeArrays(prev.facebook_headlines, adAsset.facebook_headlines),
        facebook_bodies: mergeArrays(prev.facebook_bodies, adAsset.facebook_bodies),
        facebook_description: adAsset.facebook_description ?? prev.facebook_description,
        facebook_call_to_action_type: adAsset.facebook_call_to_action_type ?? prev.facebook_call_to_action_type,
        facebook_placement_settings: adAsset.facebook_placement_settings ?? prev.facebook_placement_settings,
        facebook_destination_url: adAsset.facebook_destination_url || prev.facebook_destination_url,
        msan_headlines: mergeArrays(prev.msan_headlines, adAsset.msan_headlines),
        msan_long_headline: adAsset.msan_long_headline ?? prev.msan_long_headline,
        msan_descriptions: mergeArrays(prev.msan_descriptions, adAsset.msan_descriptions),
        msan_destination_url: adAsset.msan_destination_url ?? prev.msan_destination_url,
        name: adAsset.name || prev.name
      }));
    }
  }, [adAssets]);

  const handlePlatformChange = (platforms: EnabledPlatforms) => {
    setFormData((prev) => ({
      ...prev,
      facebook_placement_settings: {
        ...prev.facebook_placement_settings,
        platforms
      }
    }));
  };

  const handlePositionsChange = (
    platform: keyof Omit<FacebookPlacementSettings, "platforms">,
    positions: PositionsMap[PublisherPlatforms]
  ) => {
    setFormData((prev) => ({
      ...prev,
      facebook_placement_settings: {
        ...prev.facebook_placement_settings,
        [platform]: positions
      }
    }));
  };

  const mergeArrays = (defaultArray: string[], incomingArray: string[] | undefined): string[] => {
    if (!incomingArray) return defaultArray;
    return defaultArray.map((defaultValue, index) => {
      return index < incomingArray.length ? incomingArray[index] : defaultValue;
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setUpdating(true);
    let s3Path = "";
    if (file) {
      s3Path = await uploadFile(file, brandId);
    }

    const updatedData: AdAssetData = {
      google_headlines: formData.google_headlines,
      google_long_headline: formData.google_long_headline,
      google_descriptions: formData.google_descriptions,
      google_call_to_action_type: formData.google_call_to_action_type,
      google_destination_url: formData.google_destination_url,
      facebook_headlines: formData.facebook_headlines,
      facebook_bodies: formData.facebook_bodies,
      facebook_description: formData.facebook_description,
      facebook_call_to_action_type: formData.facebook_call_to_action_type,
      facebook_destination_url: formData.facebook_destination_url,
      facebook_placement_settings: formData.facebook_placement_settings,
      msan_headlines: formData.msan_headlines,
      msan_long_headline: formData.msan_long_headline,
      msan_descriptions: formData.msan_descriptions,
      msan_call_to_action_type: formData.msan_call_to_action_type,
      msan_destination_url: formData.msan_destination_url,
      name: formData.name
    };

    if (file) {
      updatedData.image_folder_path = s3Path;
    }

    const result = await bulkUpdateAdAssets(adAssetGroupId, Number(brandId), updatedData);

    setUpdating(false);

    result.match(
      (error) => {
        if (error.tag === "traceError") console.error(error.trace);

        openDialog(ErrorDialog, {
          title: "Error",
          error
        });
      },
      () => {
        onClose(true);
      }
    );
  };

  const handleInputChange = (field: keyof AdAssetData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTrackingIdChange = (field: keyof AdAssetData, newValue: string) => {
    const protectedStringIndex = newValue.indexOf(TRACKING_ID_PLACEHOLDER);
    if (protectedStringIndex === -1) {
      setFormData((prev) => ({ ...prev, [field]: TRACKING_ID_PLACEHOLDER }));
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: newValue }));
  };

  const handleListInputChange = (field: keyof AdAssetData, index: number, value: string) => {
    setFormData((prev) => {
      const newList = [...(prev[field] as string[])];
      newList[index] = value;
      return { ...prev, [field]: newList };
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const fileName = event.target.files[0].name;
      const extension = fileName.split(".").pop()?.toLowerCase();
      if (extension !== "zip") {
        openDialog(ConfirmationDialog, {
          title: "Error",
          description: "File must be a '.zip' file",
          showCancel: false,
          danger: true,
          primaryButtonText: "Ok"
        });
      } else {
        setFile(event.target.files[0]);
      }
    }
  };

  const renderGoogleFields = () => (
    <>
      <DestinationUrlField
        label="Google Destination URL"
        value={formData.google_destination_url || ""}
        field="google_destination_url"
        isTrackingIdIncluded={trackingIdInclusion.google_destination_url}
        onValueChange={handleDestinationUrlChange}
        onTrackingIdInclusionChange={handleTrackingIdInclusionChange}
        dataDogIdentifier="bulk update google destination url"
      />
      {formData.google_headlines.map((headline, index) => (
        <TextField
          key={`google-headline-${index}`}
          label={`Google Headline ${index + 1}`}
          value={headline}
          onChange={(e) => handleListInputChange("google_headlines", index, e.target.value)}
          fullWidth
          dataDogIdentifier="bulk update google headline"
        />
      ))}
      <TextField
        label="Google Long Headline"
        value={formData.google_long_headline}
        onChange={(e) => handleInputChange("google_long_headline", e.target.value)}
        fullWidth
        dataDogIdentifier="bulk update google long headline"
      />
      {formData.google_descriptions.map((description, index) => (
        <TextField
          key={`google-description-${index}`}
          label={`Google Description ${index + 1}`}
          value={description}
          onChange={(e) => handleListInputChange("google_descriptions", index, e.target.value)}
          fullWidth
          dataDogIdentifier="bulk update google description"
        />
      ))}
      <Select
        value={formData.google_call_to_action_type}
        onChange={(e) => handleInputChange("google_call_to_action_type", e.target.value as string)}
        displayEmpty
        disabled={isLoadingGoogleCTATypes}
      >
        <MenuItem value="">Google Call To Action</MenuItem>
        {isLoadingGoogleCTATypes ? (
          <MenuItem disabled>Loading CTA types...</MenuItem>
        ) : googleCtaTypesError ? (
          <MenuItem disabled>Error loading CTA types</MenuItem>
        ) : (
          googleCtaTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))
        )}
      </Select>
    </>
  );

  const renderMsanFields = () => (
    <>
      <DestinationUrlField
        label="MSAN Destination URL"
        value={formData.msan_destination_url || ""}
        field="msan_destination_url"
        isTrackingIdIncluded={trackingIdInclusion.msan_destination_url}
        onValueChange={handleDestinationUrlChange}
        onTrackingIdInclusionChange={handleTrackingIdInclusionChange}
        dataDogIdentifier="bulk update msan destination url"
      />
      {formData.msan_headlines.map((headline, index) => (
        <TextField
          key={`msan-headline-${index}`}
          label={`MSAN Headline ${index + 1}`}
          value={headline}
          onChange={(e) => handleListInputChange("msan_headlines", index, e.target.value)}
          fullWidth
          dataDogIdentifier="bulk update msan headline"
        />
      ))}
      <TextField
        label="MSAN Long Headline"
        value={formData.msan_long_headline}
        onChange={(e) => handleInputChange("msan_long_headline", e.target.value)}
        fullWidth
        dataDogIdentifier="bulk update msan long headline"
      />
      {formData.msan_descriptions.map((description, index) => (
        <TextField
          key={`msan-description-${index}`}
          label={`MSAN Description ${index + 1}`}
          value={description}
          onChange={(e) => handleListInputChange("msan_descriptions", index, e.target.value)}
          fullWidth
          dataDogIdentifier="bulk update msan description"
        />
      ))}
      <Select
        value={formData.msan_call_to_action_type}
        onChange={(e) => handleInputChange("msan_call_to_action_type", e.target.value as string)}
        displayEmpty
        disabled={isLoadingMSANCTATypes}
      >
        <MenuItem value="">MSAN Call To Action</MenuItem>
        {isLoadingMSANCTATypes ? (
          <MenuItem disabled>Loading CTA types...</MenuItem>
        ) : msanCtaTypesError ? (
          <MenuItem disabled>Error loading CTA types</MenuItem>
        ) : (
          msanCtaTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))
        )}
      </Select>
    </>
  );

  const renderMetaFields = () => (
    <>
      <DestinationUrlField
        label="Facebook Destination URL"
        value={formData.facebook_destination_url || ""}
        field="facebook_destination_url"
        isTrackingIdIncluded={trackingIdInclusion.facebook_destination_url}
        onValueChange={handleDestinationUrlChange}
        onTrackingIdInclusionChange={handleTrackingIdInclusionChange}
        dataDogIdentifier="bulk update meta destination url"
      />
      {formData.facebook_bodies.map((body, index) => (
        <TextField
          key={`facebook-body-${index}`}
          label={`Meta Primary Text ${index + 1}`}
          value={body}
          onChange={(e) => handleListInputChange("facebook_bodies", index, e.target.value)}
          fullWidth
          dataDogIdentifier="bulk update meta primary text"
        />
      ))}
      {formData.facebook_headlines.map((headline, index) => (
        <TextField
          key={`facebook-headline-${index}`}
          label={`Meta Headline ${index + 1}`}
          value={headline}
          onChange={(e) => handleListInputChange("facebook_headlines", index, e.target.value)}
          fullWidth
          dataDogIdentifier="bulk update meta headline"
        />
      ))}
      <TextField
        label="Meta Description"
        value={formData.facebook_description}
        onChange={(e) => handleInputChange("facebook_description", e.target.value)}
        fullWidth
        dataDogIdentifier="bulk update meta description"
      />
      <Select
        value={formData.facebook_call_to_action_type}
        onChange={(e) => handleInputChange("facebook_call_to_action_type", e.target.value as string)}
        displayEmpty
        disabled={isLoadingMetaCTATypes}
      >
        <MenuItem value="">Meta Call To Action</MenuItem>
        {isLoadingMetaCTATypes ? (
          <MenuItem disabled>Loading CTA types...</MenuItem>
        ) : metaCtaTypesError ? (
          <MenuItem disabled>Error loading CTA types</MenuItem>
        ) : (
          metaCtaTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))
        )}
      </Select>
      <Box mt={2}>
        <Flex
          alignItems="center"
          gap={1}
          onClick={() => setIsPlacementExpanded(!isPlacementExpanded)}
          style={{ cursor: "pointer" }}
        >
          {isPlacementExpanded ? "-" : "+"}
          <Typography variant="subtitle1">Meta Placement Settings</Typography>
        </Flex>

        {isPlacementExpanded && (
          <Box mt={2}>
            <PlatformCheckboxes
              platforms={formData.facebook_placement_settings.platforms}
              onChange={handlePlatformChange}
              positionsData={formData.facebook_placement_settings}
              onPositionsChange={handlePositionsChange}
            />
          </Box>
        )}
      </Box>
    </>
  );

  if (adAssetError) {
    return <Typography color="error">Error loading ad asset data: {adAssetError}</Typography>;
  }

  return isLoadingAdAsset ? (
    <Typography>Loading ad asset data...</Typography>
  ) : (
    <FormDialog
      open={open}
      onClose={(e) => {
        if (window.confirm("Are you sure you want to close this dialog?")) {
          onClose(e);
        }
      }}
      title="Edit Ad Details (bulk)"
      primaryButtonText="Update"
      onSubmit={handleSubmit}
      isPrimaryButtonDisabled={updating}
      dataDogIdentifier="edit ad details (bulk)"
    >
      <Flex flexDirection="column" gap={2}>
        <Box mb={4}>
          <input type="file" onChange={handleFileChange} style={{ display: "none" }} id="file-upload-input" />
          <label htmlFor="file-upload-input">
            <Button variant="contained" component="span" dataDogIdentifier="edit ad details (bulk) upload zip file">
              Upload Zip File
            </Button>
          </label>
          {file && (
            <Typography variant="body2" mt={1}>
              Selected file: {file.name}
            </Typography>
          )}
        </Box>

        {!!adAssets?.length && (
          <>
            <TextField
              label="Ad name"
              value={formData.name}
              onChange={(e) => handleTrackingIdChange("name", e.target.value)}
              fullWidth
              dataDogIdentifier="bulk update ad name"
            />
            {adAssetGroup?.platforms?.includes(PlatformEnum.GDN) && renderGoogleFields()}
            {adAssetGroup?.platforms?.includes(PlatformEnum.MSAN) && renderMsanFields()}
            {adAssetGroup?.platforms?.includes(PlatformEnum.META) && renderMetaFields()}
          </>
        )}
      </Flex>
    </FormDialog>
  );
};

export default BulkUpdateAdAssetDialog;
